import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';
import getNotificationPostId from './getNotificationPostId';
import * as styles from './NotificationItemV2.module.scss';
import Avatar from '../components/Avatar';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import {
  NotificationReadStatus,
  NotificationStatus,
  NotificationType,
} from '../graphql/generated/graphql';
import archiveIcon from '../icons/non-prefilled/12x12/archive.svg';
import commentIcon from '../icons/non-prefilled/12x12/comment.svg';
import publishIcon from '../icons/non-prefilled/12x12/publish.svg';
import shareIcon from '../icons/non-prefilled/12x12/share.svg';
import thankIcon from '../icons/non-prefilled/16x16/thanks.svg';
import verifyIcon from '../icons/non-prefilled/16x16/verified.svg';

import CommentPreviewText from '../post/comment/CommentPreviewText';
import PostDetailPreviewPopOver from '../post/PostDetailPreviewPopOver';
import { useGetUser } from '../user/useAllUsers';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import Link from '../utils/router/Link';
import getFirstName from '../utils/text/getFirstName';
import notificationPath from '../utils/url/notificationPath';

const SET_NOTIFICATIONS_STATUS_MUTATION = gql(/** @complexity 4 */ `
  mutation setNotificationsStatusV2(
    $ids: [ID!]
    $status: NotificationReadStatus!
  ) {
    markNotifications(ids: $ids, status: $status) {
      id
      status
    }
  }
`);

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationItemV2_notifications on Notification {
    id
    status
    type
    insertedAt
    actor {
      ...useGetUser_user
    }
    ... on ReactionNotification {
      reaction {
        id
        emoji
        thread {
          id
          context
          post {
            id
            title
            publishedAt
          }
        }
      }
    }
    ... on CommentNotification {
      comment {
        id
        content
        thread {
          id
          post {
            id
            title
            publishedAt
          }
        }
      }
    }
    ... on PostNotification {
      post {
        id
        title
        publishedAt
      }
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...getNotificationPostId_notification
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...notificationPath_notifications
  }
`);

interface NotificationItemProps {
  notification: DocumentType<typeof NOTIFICATIONS_FRAGMENT>;
  onNavigate?: () => void;
}

const NotificationItemV2 = ({
  notification,
  onNavigate,
}: NotificationItemProps) => {
  const getUser = useGetUser();
  const isLarge = useIsSize(Sizes.LG);

  const [setStatus] = useMutation(SET_NOTIFICATIONS_STATUS_MUTATION);

  const isRead = notification.status === NotificationStatus.Read;
  const actor = getUser(notification.actor);
  const notificationPostId = getNotificationPostId(notification);

  const toggleStatus = () => {
    return setStatus({
      variables: {
        ids: [notification.id],
        status: isRead
          ? NotificationReadStatus.Unread
          : NotificationReadStatus.Read,
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        markNotifications: [
          {
            __typename: notification.__typename,
            id: notification.id,
            status: isRead ? NotificationStatus.Sent : NotificationStatus.Read,
          },
        ],
      },
    });
  };

  const view = getNotificationItemView(notification, actor?.name ?? '');

  if (!view) {
    return null;
  }

  const link = (
    <Link
      to={notificationPath([notification])}
      className={styles.notificationItem}
      onClick={(e) => {
        if (!isRead) {
          toggleStatus();
        }
        if (onNavigate && !e.metaKey && !e.ctrlKey) {
          onNavigate();
        }
      }}
    >
      {view}
    </Link>
  );

  if (isLarge && notificationPostId) {
    return (
      <div className={styles.container}>
        <PostDetailPreviewPopOver postId={notificationPostId} placement="right">
          {link}
        </PostDetailPreviewPopOver>
      </div>
    );
  }

  return <div className={styles.container}>{link}</div>;
};

const getNotificationItemView = (
  notification: DocumentType<typeof NOTIFICATIONS_FRAGMENT>,
  fullName: string,
): ReactNode | null => {
  if (
    !notification ||
    notification.type === NotificationType.InviteAccept ||
    notification.type === NotificationType.PostEdit
  ) {
    return null;
  }

  const post = (() => {
    switch (notification.__typename) {
      case 'CommentNotification':
        return notification.comment?.thread.post;
      case 'ReactionNotification':
        return notification.reaction?.thread.post;
      case 'PostNotification':
        return notification.post;
      default:
        return null;
    }
  })();

  const postTitle = post?.title && (
    <span className={styles.postTitle}>{post.title}</span>
  );

  const commentPreview = notification.__typename === 'CommentNotification' && (
    <CommentPreviewText maxLines={1} context={notification.comment?.content} />
  );

  const postIconStyle = post?.publishedAt ? styles.postIcon : styles.draftIcon;
  const actorFirstName = getFirstName(fullName) ?? '';

  switch (notification.type) {
    case NotificationType.CommentCreate:
    case NotificationType.CommentReply:
    case NotificationType.CommentMention:
      return (
        <NotificationItemView
          notification={notification}
          icon={commentIcon}
          iconClassName={styles.commentIcon}
          firstRow={commentPreview}
          secondRow={<>in {postTitle}</>}
        />
      );
    case NotificationType.CommentResolve:
      return (
        <NotificationItemView
          notification={notification}
          icon={commentIcon}
          iconClassName={styles.mutedIcon}
          firstRow={commentPreview}
          secondRow={<>in {postTitle}</>}
        />
      );
    case NotificationType.ReactionCreate:
      return notification.__typename === 'ReactionNotification' ? (
        <NotificationItemView
          notification={notification}
          icon={notification.reaction?.emoji ?? ''}
          firstRow={
            <CommentPreviewText
              maxLines={1}
              context={notification.reaction?.thread.context}
            />
          }
          secondRow={<>in {postTitle}</>}
        />
      ) : null;
    case NotificationType.PostThank:
      return (
        <NotificationItemView
          notification={notification}
          icon={thankIcon}
          iconClassName={postIconStyle}
          firstRow={postTitle}
          secondRow={<>{actorFirstName} gave you thanks</>}
        />
      );
    case NotificationType.PostPublish:
      return (
        <NotificationItemView
          notification={notification}
          icon={publishIcon}
          iconClassName={postIconStyle}
          firstRow={postTitle}
          secondRow={<>{actorFirstName} published this post</>}
        />
      );
    case NotificationType.PostArchive:
      return (
        <NotificationItemView
          notification={notification}
          icon={archiveIcon}
          firstRow={postTitle}
          secondRow={<>{actorFirstName} archived this post</>}
        />
      );
    case NotificationType.PostShare:
      return (
        <NotificationItemView
          notification={notification}
          icon={shareIcon}
          iconClassName={postIconStyle}
          firstRow={postTitle}
          secondRow={<>{actorFirstName} shared this post</>}
        />
      );
    case NotificationType.VerificationReminder:
      return (
        <NotificationItemView
          notification={notification}
          icon={verifyIcon}
          iconClassName={styles.verificationReminderIcon}
          firstRow={postTitle}
          secondRow="Verification will expire soon" // TODO: there's no information about time to expire in the API
        />
      );
    case NotificationType.VerificationExpired:
      return (
        <NotificationItemView
          notification={notification}
          icon={verifyIcon}
          iconClassName={styles.mutedIcon}
          firstRow={postTitle}
          secondRow="Verification has expired"
        />
      );
    case NotificationType.VerificationRequest:
      return (
        <NotificationItemView
          notification={notification}
          icon={verifyIcon}
          iconClassName={styles.verificationRequestIcon}
          firstRow={postTitle}
          secondRow={<>{actorFirstName} requested verification</>} // TODO there's no information about frequency in the API
        />
      );
    default:
      exhaustiveMatchGuard(notification.type);
      return null;
  }
};

interface NotificationItemViewProps {
  notification: DocumentType<typeof NOTIFICATIONS_FRAGMENT>;
  icon: string;
  iconClassName?: string;
  firstRow: ReactNode;
  secondRow: ReactNode;
  action?: ReactNode;
}

const NotificationItemView = ({
  notification,
  icon,
  iconClassName,
  firstRow,
  secondRow,
  action,
}: NotificationItemViewProps) => {
  const getUser = useGetUser();
  const isRead = notification.status === NotificationStatus.Read;
  const actor = getUser(notification.actor);

  return (
    <>
      <div className={styles.avatarContainer}>
        {!isRead && <div className={styles.unreadIndicator} />}
        {actor && <Avatar size={34} user={actor} />}
        <SVGIcon
          size={14}
          className={classNames(styles.actionIcon, iconClassName)}
        >
          {icon}
        </SVGIcon>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.row}>
          <div className={styles.firstRow}>{firstRow}</div>
          <div className={styles.timestamp}>
            <Timestamp date={notification.insertedAt} />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.secondRow}>{secondRow}</div>
          <div className={styles.action}>{action}</div>
        </div>
      </div>
    </>
  );
};

export default NotificationItemV2;
